import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES } from '../lib/tables';

const typeLabel = {
  integration_code_generated: 'Cod generat',
  integration_code_used: 'Cod folosit',
  integration_link_created: 'Link creat',
  integration_link_revoked: 'Link revocat',
  srbhub_credit_import: 'Credit importat',
};

const typeColor = {
  integration_code_generated: 'info',
  integration_code_used: 'primary',
  integration_link_created: 'success',
  integration_link_revoked: 'warning',
  srbhub_credit_import: 'success',
};

export default function AuditIntegrations() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [names, setNames] = useState({});
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [details, setDetails] = useState({ open: false, log: null });

  const loadData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from(TABLES.audit_log)
        .select('*')
        .or('entity_type.like.integration_%,entity_type.like.srbhub_%')
        .order('created_at', { ascending: false })
        .limit(300);

      if (error) throw error;
      const rows = data || [];
      setLogs(rows);

      const ids = [
        ...new Set(rows.map((r) => r.new_values?.id_elev).filter((v) => typeof v === 'string' && v)),
      ];
      if (ids.length > 0) {
        const { data: profiles } = await supabase
          .from(TABLES.profiles)
          .select('id, nume, prenume')
          .in('id', ids);
        const map = {};
        (profiles || []).forEach((p) => {
          map[p.id] = [p.nume, p.prenume].filter(Boolean).join(' ');
        });
        setNames(map);
      }
    } catch (err) {
      console.error('Error loading integration audit:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const types = useMemo(() => [...new Set(logs.map((l) => l.entity_type))].sort(), [logs]);

  const filtered = useMemo(() => {
    let rows = [...logs];
    if (typeFilter !== 'all') rows = rows.filter((l) => l.entity_type === typeFilter);
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      rows = rows.filter((l) => {
        const name = names[l.new_values?.id_elev] || '';
        return (
          name.toLowerCase().includes(term) ||
          l.action_description?.toLowerCase().includes(term) ||
          l.entity_id?.toLowerCase().includes(term) ||
          l.new_values?.external_user_id?.toLowerCase().includes(term)
        );
      });
    }
    return rows;
  }, [logs, typeFilter, search, names]);

  const getStudentName = (log) => {
    const id = log.new_values?.id_elev;
    if (id && names[id]) return names[id];
    return id || log.entity_id || '-';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Audit Integrări SRBHub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Coduri generate/folosite, linkuri create/revocate și credite importate.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TextField
          size="small"
          label="Caută elev / descriere / ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />,
          }}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Tip eveniment</InputLabel>
          <Select value={typeFilter} label="Tip eveniment" onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="all">Toate</MenuItem>
            {types.map((t) => (
              <MenuItem key={t} value={t}>
                {typeLabel[t] || t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button onClick={loadData} startIcon={<RefreshIcon />}>
          Reîncarcă
        </Button>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Elev</TableCell>
              <TableCell>Descriere</TableCell>
              <TableCell align="center">Detalii</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell>
                  <Typography variant="caption">
                    {log.created_at ? new Date(log.created_at).toLocaleString('ro-RO') : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={typeLabel[log.entity_type] || log.entity_type}
                    color={typeColor[log.entity_type] || 'default'}
                  />
                </TableCell>
                <TableCell>{getStudentName(log)}</TableCell>
                <TableCell>
                  <Typography variant="body2">{log.action_description}</Typography>
                  {log.user_name && (
                    <Typography variant="caption" color="text.secondary">
                      actor: {log.user_name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Button size="small" onClick={() => setDetails({ open: true, log })}>
                    Vezi
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Nu există evenimente de integrare.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={details.open}
        onClose={() => setDetails({ open: false, log: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Detalii eveniment</DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid',
              borderColor: 'divider',
              fontSize: 12,
              overflow: 'auto',
            }}
          >
            {JSON.stringify(details.log?.new_values ?? {}, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetails({ open: false, log: null })}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
